/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';

import { Breadcrumbs, Grid, Typography } from '@mui/material';

import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { ProjectApiService } from '../../services/ProjectApiService';
import { RootState } from '../../store';
import { fetchOrganization } from '../../store/organization/actions';
import { ApiClientError } from '../../types/api/ApiClientError';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchProject } from '../../utils/queryFns';
import { reactToDeleteProject } from '../../utils/reactions';
import { useApiValidation } from '../../utils/useApiValidation';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useIsMounted } from '../../utils/useIsMounted';
import { useProjectPermissions } from '../../utils/useProjectPermissions';
import { ProjectPageHeader } from './components/ProjectPageHeader/ProjectPageHeader';
import { ServiceList } from './components/ServiceList';
import { createProjectPageStyles } from './ProjectPage.styles';

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  organizationDetails: state.organizationDetails,
});
const connector = connect(mapStateToProps);

type ProjectPageProps = ConnectedProps<typeof connector>;
export type ProjectPagePathParamsType = {
  organization_id: string;
  project_id: string;
};

const ProjectPageComponent = memo<ProjectPageProps>(props => {
  const { t } = useTranslation(Namespaces.Navigation);
  const dispatch = useDispatch();
  const classes = createProjectPageStyles();
  const { organizationDetails } = props;
  const navigate = useNavigate();
  const params = useParams<ProjectPagePathParamsType>();
  const organizationId = params.organization_id!;
  const projectId = params.project_id!;
  const organization = organizationDetails?.options?.payload || null;
  const { errorMessage, setApiError } = useApiValidation();

  const permissions = useProjectPermissions(organizationId, projectId);

  const isMounted = useIsMounted();

  const onChangeProject = () => {
    if (!isMounted.current) {
      return;
    }
    if (organizationId) {
      dispatch(fetchOrganization(organizationId));
    }
    setNeedUpdate(!needUpdate);
  };

  const handleManageClick = () => {
    void navigate(
      prepareRoutePath(BasicRoutes.MANAGE_PROJECT, {
        ':organization_id': organizationId,
        ':project_id': projectId,
      }),
    );
  };

  const onConfirmDelete = async () => {
    setIsOpenModal(false);
    try {
      await ProjectApiService.deleteProject({
        organizationId,
        projectId,
      });
      void reactToDeleteProject({ organizationId });
      void navigate(
        prepareRoutePath(BasicRoutes.ORGANIZATION_PAGE, {
          ':organization_id': organizationId,
        }),
      );
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        setApiError(error);
      }
      setIsOpenErrorModal(true);
    }
  };

  const [needUpdate, setNeedUpdate] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const [isOpenPermissionsErrorModal, setIsOpenPermissionsErrorModal] = useState(false);

  useEffect(() => {
    if (!permissions.read_project) {
      setIsOpenPermissionsErrorModal(true);
    }
  }, [permissions]);

  const projectQuery = useQuery([fetchProject.key, { organizationId, projectId }], fetchProject.fn);

  const project = useMemo(() => projectQuery.data?.payload ?? null, [projectQuery.data]);

  useDocumentTitle(project?.name);

  const onOkPermissionsError = () => {
    setIsOpenPermissionsErrorModal(false);
    void navigate(-1);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} classes={{ item: classes.wrapperBreadcrumbs }}>
          {organizationId ? (
            <Breadcrumbs>
              <RouterLink to="/">{t('navigation:page.home')}</RouterLink>
              <RouterLink
                to={prepareRoutePath(BasicRoutes.ORGANIZATION_PAGE, {
                  ':organization_id': organizationId,
                })}
              >
                {organization?.name}
              </RouterLink>
              <Typography>{project?.name}</Typography>
            </Breadcrumbs>
          ) : (
            <Breadcrumbs>
              <RouterLink to="/">{t('navigation:page.home')}</RouterLink>
              <Typography>{project?.name}</Typography>
            </Breadcrumbs>
          )}
        </Grid>
        <Grid item xs={12} classes={{ item: classes.wrapperProjectPageHeader }}>
          <ProjectPageHeader
            project={project}
            isFetching={project === null}
            organizationId={organizationId}
            projectId={projectId}
            handleManageClick={handleManageClick}
            onChangeProject={onChangeProject}
            onDeleteProject={() => setIsOpenModal(true)}
          />
        </Grid>
        <Grid item xs={12}>
          <ServiceList user={props.auth.user!} organizationId={organizationId} projectId={projectId} />
        </Grid>
      </Grid>
      <SimpleConfirmModal
        open={isOpenModal}
        message={t(`${Namespaces.Common}:confirmation.delete_project`, {
          name: project?.name,
        })}
        onCancel={() => setIsOpenModal(false)}
        onConfirm={onConfirmDelete}
      />
      <SimpleNotifyModal
        open={isOpenErrorModal}
        message={t(
          [`${Namespaces.Common}:delete_entity_error.${errorMessage}`, `${Namespaces.Common}:errors.unexpected_error`],
          { entity_name: project?.name },
        )}
        onOk={() => setIsOpenErrorModal(false)}
      />
      <SimpleNotifyModal
        open={isOpenPermissionsErrorModal}
        message={t(`${Namespaces.Rbac}:project.error`)}
        onOk={onOkPermissionsError}
      />
    </>
  );
});

export const ProjectPage = connector(ProjectPageComponent);
